import React, { useMemo, useCallback, useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useGridScrollUtils } from "utils/DataGrid";
import { CONTENT_FEED_MAPPING_COLUMNS } from "widgets/grid/columns/ContentFeedMappings";
import { DataGrid, Button } from "rhombus-react";
import ContentFeedFooter from "routes/contentfeeds/ContentFeedFooter";
import ContentFeedMappingsValidations from "routes/contentfeeds/ContentFeedMappingsValidations";
import { FormSection, arrayPush, arrayRemove, change, getFormValues } from "redux-form";
import { useDispatch, useSelector } from "react-redux";
import { FEED_CREATION_FORM } from "forms/list";
import { DEFAULT_FEED_MAPPINGS } from "routes/contentfeeds/constants";
import { getValidationFieldErrors, getValidationMessages } from "utils/ContentFeedMappings";
import ProgressContainer from "widgets/ProgressContainer";
import { fetchSpreadsheetAutoMappings, fetchSpreadsheetHeaders } from "reducers/actions/ContentFeedActions";
import { normalizeAutoMappings, VERSION_NAME_MAPPING_KEY } from "utils/ContentFeedCreation";

import "./ContentFeedMappingsGrid.css";

const HEADER_HEIGHT = 50;
const ROW_HEIGHT = 50;
const isVersionNameMapping = (mappingName) => {
	return mappingName?.trim() === VERSION_NAME_MAPPING_KEY;
};

export default function ContentFeedMappingsGrid({
	libraryId,
	onCancel,
	onBack,
	onSave,
	isSaveLoading = false,
}) {
	const dispatch = useDispatch();
	const formValues = useSelector(state => getFormValues(FEED_CREATION_FORM)(state));
	const sourceUrl = formValues?.sourceUrl;
	const selectedSheetId = formValues?.sheetName?.value;
	const hasAutoMappingEnabled = Boolean(formValues?.autoMapping);
	const feedMappings = formValues?.feedMappings || Object.values(DEFAULT_FEED_MAPPINGS);
	const [mappingElementOptions, setMappingElementOptions] = useState([]);
	const [isLoadingMappings, setIsLoadingMappings] = useState(false);
	const [validationMessages, setValidationMessages] = useState([]);
	const [validationFieldErrors, setValidationFieldErrors] = useState({
		mappingNameRowIndexesWithErrors: [],
		mappingElementRowIndexesWithErrors: [],
	});
	const dataGridRef = useGridScrollUtils([feedMappings.length]);

	useEffect(() => {
		if (!sourceUrl) {
			return;
		}

		setIsLoadingMappings(true);

		const headersPromise = Promise.resolve(dispatch(fetchSpreadsheetHeaders(sourceUrl, selectedSheetId)))
			.then((response) => {
				const headers = response?.payload?.data?.headers;
				if (Array.isArray(headers) && headers.length > 0) {
					setMappingElementOptions(headers);
				}
			});

		if (!hasAutoMappingEnabled) {
			headersPromise.finally(() => {
				setIsLoadingMappings(false);
			});
			return;
		}

		const autoMappingsPromise = Promise.resolve(dispatch(fetchSpreadsheetAutoMappings(sourceUrl, selectedSheetId)))
			.then((response) => {
				const normalizedMappings = normalizeAutoMappings(response?.payload?.data);
				dispatch(change(FEED_CREATION_FORM, "feedMappings", normalizedMappings));
			});

		Promise.all([headersPromise, autoMappingsPromise]).finally(() => {
			setIsLoadingMappings(false);
		});
	}, [dispatch, hasAutoMappingEnabled, selectedSheetId, sourceUrl]);
	useEffect(() => {
		feedMappings.forEach((mapping, rowIndex) => {
			if (isVersionNameMapping(mapping?.name) && mapping?.allowEmpty) {
				dispatch(change(FEED_CREATION_FORM, `feedMappings[${rowIndex}].allowEmpty`, false));
			}
		});
	}, [dispatch, feedMappings]);

	const handleDeleteMapping = useCallback((mappingId, rowIndex) => {
		if (Number.isInteger(rowIndex)) {
			dispatch(arrayRemove(FEED_CREATION_FORM, "feedMappings", rowIndex));
		}
	}, [dispatch]);
	const handleAddMapping = useCallback(() => {
		const maxId = feedMappings.reduce((max, mapping) => Math.max(max, mapping.id), 0);
		const newMapping = {
			id: maxId + 1,
			name: "",
			mappingElement: undefined,
			allowEmpty: false,
		};
		dispatch(arrayPush(FEED_CREATION_FORM, "feedMappings", newMapping));
	}, [dispatch, feedMappings]);
	const handleSave = useCallback(() => {
		const messages = getValidationMessages(feedMappings);
		const fieldErrors = getValidationFieldErrors(feedMappings);
		setValidationMessages(messages);
		setValidationFieldErrors(fieldErrors);
		if (messages.length) {
			return;
		}
		onSave();
	}, [feedMappings, onSave]);

	const mappedContentFeedsMappings = useMemo(() => {
		return feedMappings.map((mappings, rowIndex) => ({
			...mappings,
			rowIndex,
			libraryId,
			onDeleteMapping: handleDeleteMapping,
			mappingElementOptions,
			hasMappingNameError: validationFieldErrors.mappingNameRowIndexesWithErrors.includes(rowIndex),
			hasMappingElementError: validationFieldErrors.mappingElementRowIndexesWithErrors.includes(rowIndex),
		}));
	}, [feedMappings, libraryId, handleDeleteMapping, validationFieldErrors, mappingElementOptions]);

	const getRow = (rowID) => {
		if (rowID === -1) {
			return;
		}
		return mappedContentFeedsMappings[rowID];
	};

	const gridHeight = useMemo(() => {
		const rowCount = mappedContentFeedsMappings.length;
		return rowCount === 0 ? HEADER_HEIGHT + ROW_HEIGHT : HEADER_HEIGHT + (rowCount * ROW_HEIGHT);
	}, [mappedContentFeedsMappings.length]);

	const dataGridProps = {
		columns: CONTENT_FEED_MAPPING_COLUMNS,
		rowGetter: getRow,
		rowsCount: mappedContentFeedsMappings.length,
		rowHeight: ROW_HEIGHT,
		headerRowHeight: HEADER_HEIGHT,
		onGridSort: () => {},
		enableCellAutoFocus: false,
	};

	return (
		<FormSection name="feedMappings">
			<div className="content-feed-mappings-grid">
				<div className="grid-header-actions">
					<Button
						name="addButton"
						onClick={handleAddMapping}
						className="add-mapping-button"
						disabled={isLoadingMappings}
					>
						+ Add New Mapping
					</Button>
				</div>
				<ContentFeedMappingsValidations messages={validationMessages} />
				<ProgressContainer isLoading={isLoadingMappings}>
					<div ref={dataGridRef}>
						<DataGrid
							{...dataGridProps}
							minHeight={gridHeight}
						/>
					</div>
				</ProgressContainer>
				<ContentFeedFooter
					saveTitle="Save Bulk Sheet"
					backTitle="Back"
					onCancel={onCancel}
					onBack={onBack}
					onSave={handleSave}
					isSaveLoading={isSaveLoading}
				/>
			</div>
		</FormSection>
	);
}

ContentFeedMappingsGrid.displayName = "ContentFeedMappingsGrid";

ContentFeedMappingsGrid.propTypes = {
	libraryId: PropTypes.string.isRequired,
	onCancel: PropTypes.func.isRequired,
	onBack: PropTypes.func.isRequired,
	onSave: PropTypes.func.isRequired,
	isSaveLoading: PropTypes.bool,
};
